import { useEffect } from 'react';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  ReportBody,
  Report,
  Container,
  handleError,
  SecureBeforeYouClickBody,
  SecureBeforeYouClickResponse,
  TestLinksResponse,
  TestLinksBody,
  ConfigurationResponse,
  getUserEmail,
  getDisplayLanguage,
} from '@utils-global';
import { AxiosResponse } from 'axios';
import { routes } from '../constants';
import { useConfigurationStorage } from '../storages';
import { configurationQueryOptions } from './configurationQuery';

export const useReportMail = (): UseMutationResult<
  AxiosResponse<Report>,
  Error,
  ReportBody,
  unknown
> =>
  useMutation({
    mutationFn: (body) =>
      Container.phishedClient.post(routes.phished.reportMail, {
        ...body,
        locale: getDisplayLanguage(),
      }),
    onError: (error) => handleError(error),
  });

export const useSecureBeforeYouClick = (): UseMutationResult<
  AxiosResponse<SecureBeforeYouClickResponse>,
  Error,
  SecureBeforeYouClickBody,
  unknown
> =>
  useMutation({
    mutationFn: (body) =>
      Container.zimClient.post(routes.phished.secureBeforeYouClick, {
        ...body,
        locale: getDisplayLanguage(),
      }),
    onError: (error) => handleError(error),
  });

export const useTestLinks = (): UseMutationResult<
  AxiosResponse<TestLinksResponse>,
  Error,
  TestLinksBody,
  unknown
> =>
  useMutation({
    mutationFn: (body) => Container.phishedClient.post(routes.phished.testLinks, body),
    onError: (error) => handleError(error),
  });

export const useLoadCachedConfiguration = () => {
  const { setConfiguration } = useConfigurationStorage();
  const { data } = useQuery({
    ...configurationQueryOptions(() => Container.phishedClient, getUserEmail()),
    enabled: false,
  });

  useEffect(() => {
    if (data) {
      setConfiguration(data.data);
    }
  }, [data]);
};

export const useLoadConfiguration = (): UseQueryResult<
  AxiosResponse<ConfigurationResponse>,
  Error
> => {
  const { setConfiguration } = useConfigurationStorage();
  const email = getUserEmail();

  const result = useQuery({
    ...configurationQueryOptions(() => Container.phishedClient, email),
    retry: false,
  });

  useEffect(() => {
    if (result.data) {
      setConfiguration(result.data.data);
    }
  }, [result.data]);

  if (result.isError) {
    handleError(result.error);
  }

  return result;
};
